// useProcessFlowEditor.ts
// Extracts all state and handler logic from ProcessFlowEditorInner
// to reduce cyclomatic complexity below CC ≤ 15 (AIAG-VDA requirement)
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useReactFlow, useNodesState, useEdgesState, addEdge, Node, Edge, Connection, getRectOfNodes, getTransformForBounds } from 'reactflow';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import {
    ProcessFlowNode as ProcessFlowNodeType,
    ProcessFlowEdge as ProcessFlowEdgeType,
    ProcessStepData,
    ProcessStepType,
    ConnectionType,
    generateStepNumber,
} from '../types/processFlow';
import { ProcessItem, WorkElement } from '../types';
import processFlowService, { SavedProcessFlow } from '../services/processFlowService';
import { PROCESS_FLOW_TEMPLATES } from '../components/ProcessFlow/ProcessFlowTemplates';

// ─── initial diagram ─────────────────────────────────────────────────────────
function getInitialNodes(): ProcessFlowNodeType[] {
    return [
        {
            id: 'start',
            type: 'processFlowNode',
            position: { x: 250, y: 50 },
            data: {
                label: 'Process Start',
                stepNumber: 'OP-000',
                type: ProcessStepType.START_END,
                description: 'Process entry point',
            },
        },
    ];
}

// ─── hook ─────────────────────────────────────────────────────────────────────
export function useProcessFlowEditor() {
    const { theme } = useTheme();
    const { user, hasPermission } = useAuth();
    const { activeProjectName } = useAppContext();
    const { extractFromFlow } = useAnalysis();
    const isDark = theme === 'dark';
    const reactFlowInstance = useReactFlow();

    // ── canvas state ──────────────────────────────────────────────────────────
    const [nodes, setNodes, onNodesChange] = useNodesState(getInitialNodes());
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [showTemplateMenu, setShowTemplateMenu] = useState(false);
    const [showLoadMenu, setShowLoadMenu] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showStructureTree, setShowStructureTree] = useState(false);
    const [nodeCount, setNodeCount] = useState(1);
    const [flowName, setFlowName] = useState('New Process Flow');
    const [flowId, setFlowId] = useState<string | null>(null);
    const [savedFlows, setSavedFlows] = useState<SavedProcessFlow[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    // ── RBAC ──────────────────────────────────────────────────────────────────
    const canEdit = hasPermission('process_flow.edit');
    const canCreate = hasPermission('process_flow.create');

    // ── refs ──────────────────────────────────────────────────────────────────
    const templateMenuRef = useRef<HTMLDivElement>(null);
    const loadMenuRef = useRef<HTMLDivElement>(null);
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── undo/redo ─────────────────────────────────────────────────────────────
    const [history, setHistory] = useState<{ nodes: Node[]; edges: Edge[] }[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isUndoRedo, setIsUndoRedo] = useState(false);
    const [clipboard, setClipboard] = useState<{ nodes: Node[]; edges: Edge[] } | null>(null);

    // ── structure tree data ───────────────────────────────────────────────────
    const [processItems, setProcessItems] = useState<ProcessItem[]>([]);
    const [workElements, setWorkElements] = useState<WorkElement[]>([]);

    // ── derived ───────────────────────────────────────────────────────────────
    const selectedNode = useMemo(
        () => nodes.find(n => n.id === selectedNodeId) as ProcessFlowNodeType | undefined,
        [nodes, selectedNodeId]
    );
    const selectedEdge = useMemo(
        () => edges.find(e => e.id === selectedEdgeId) as ProcessFlowEdgeType | undefined,
        [edges, selectedEdgeId]
    );

    // ── effects ───────────────────────────────────────────────────────────────
    // Track history when nodes/edges change
    useEffect(() => {
        if (isUndoRedo) { setIsUndoRedo(false); return; }
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push({ nodes: [...nodes], edges: [...edges] });
        if (newHistory.length > 50) newHistory.shift();
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [nodes, edges]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load saved flows
    useEffect(() => {
        async function loadFlows() {
            if (activeProjectName) {
                const flows = await processFlowService.getProcessFlows(activeProjectName);
                setSavedFlows(flows);
            }
        }
        loadFlows();
    }, [activeProjectName]);

    // Close menus when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (templateMenuRef.current && !templateMenuRef.current.contains(event.target as HTMLElement))
                setShowTemplateMenu(false);
            if (loadMenuRef.current && !loadMenuRef.current.contains(event.target as HTMLElement))
                setShowLoadMenu(false);
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as HTMLElement))
                setShowExportMenu(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── undo/redo callbacks ───────────────────────────────────────────────────
    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            setIsUndoRedo(true);
            const prev = history[historyIndex - 1];
            setNodes(prev.nodes);
            setEdges(prev.edges);
            setHistoryIndex(historyIndex - 1);
        }
    }, [history, historyIndex, setNodes, setEdges]);

    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            setIsUndoRedo(true);
            const next = history[historyIndex + 1];
            setNodes(next.nodes);
            setEdges(next.edges);
            setHistoryIndex(historyIndex + 1);
        }
    }, [history, historyIndex, setNodes, setEdges]);

    const handleCopy = useCallback(() => {
        const selectedNodes = nodes.filter(n => n.selected);
        const selectedEdges = edges.filter(e => e.selected);
        if (selectedNodes.length > 0) setClipboard({ nodes: selectedNodes, edges: selectedEdges });
    }, [nodes, edges]);

    const handlePaste = useCallback(() => {
        if (!clipboard || clipboard.nodes.length === 0) return;
        const offset = 50;
        const nodeIdMap = new Map<string, string>();
        const newNodes = clipboard.nodes.map(node => {
            const newId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            nodeIdMap.set(node.id, newId);
            return { ...node, id: newId, position: { x: node.position.x + offset, y: node.position.y + offset }, selected: true };
        });
        const newEdges = clipboard.edges
            .filter(edge => nodeIdMap.has(edge.source) && nodeIdMap.has(edge.target))
            .map(edge => ({
                ...edge,
                id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                source: nodeIdMap.get(edge.source)!,
                target: nodeIdMap.get(edge.target)!,
                selected: false,
            }));
        setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...newNodes]);
        setEdges(eds => [...eds, ...newEdges]);
    }, [clipboard, setNodes, setEdges]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (!(e.ctrlKey || e.metaKey)) return;
            const handlers: Record<string, () => void> = {
                z: e.shiftKey ? handleRedo : handleUndo,
                y: handleRedo,
                c: handleCopy,
                v: handlePaste,
            };
            const fn = handlers[e.key.toLowerCase()];
            if (fn) { e.preventDefault(); fn(); }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo, handleCopy, handlePaste]);

    // ── save / load ───────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!canEdit && !canCreate) { alert('You do not have permission to save process flows.'); return; }
        if (!activeProjectName) { alert('Please select a project first.'); return; }
        setIsSaving(true);
        try {
            const flow = reactFlowInstance.toObject();
            const saved = await processFlowService.saveProcessFlow(
                activeProjectName, flowName, flow, processItems, workElements, flowId || undefined
            );
            setFlowId(saved.id);
            const flows = await processFlowService.getProcessFlows(activeProjectName);
            setSavedFlows(flows);
            alert('Process flow saved successfully!');
        } catch (e: unknown) {
            alert(`Failed to save: ${(e as Error).message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleLoadFlow = (savedFlow: SavedProcessFlow) => {
        const { x = 0, y = 0, zoom = 1 } = savedFlow.flow.viewport || {};
        setNodes(savedFlow.flow.nodes || []);
        setEdges(savedFlow.flow.edges || []);
        // Always reset — avoids stale data when a flow has no items/elements saved
        setProcessItems(savedFlow.processItems ?? []);
        setWorkElements(savedFlow.workElements ?? []);
        setFlowName(savedFlow.name);
        setFlowId(savedFlow.id);
        reactFlowInstance.setViewport({ x, y, zoom });
        setShowLoadMenu(false);
    };

    const handleDeleteFlow = async (e: React.MouseEvent, flowToDelete: SavedProcessFlow) => {
        e.stopPropagation();
        if (!confirm(`Delete "${flowToDelete.name}"? This cannot be undone.`)) return;
        try {
            await processFlowService.deleteProcessFlow(flowToDelete.id);
            if (activeProjectName) {
                const flows = await processFlowService.getProcessFlows(activeProjectName);
                setSavedFlows(flows);
            }
            if (flowId === flowToDelete.id) {
                setNodes(getInitialNodes());
                setEdges([]);
                setFlowName('New Process Flow');
                setFlowId(null);
            }
        } catch (e: unknown) {
            alert(`Failed to delete: ${(e as Error).message}`);
        }
    };

    // ── export helpers ────────────────────────────────────────────────────────
    const buildExportData = (): SavedProcessFlow => ({
        id: flowId || `export-${Date.now()}`,
        projectName: activeProjectName || 'unknown',
        name: flowName,
        createdBy: user?.email || 'anonymous',
        createdAt: new Date().toISOString(),
        updatedBy: user?.email || 'anonymous',
        updatedAt: new Date().toISOString(),
        version: 1,
        flow: reactFlowInstance.toObject(),
    });

    const handleExportJSON = () => {
        const data = buildExportData();
        const json = processFlowService.exportAsJSON(data);
        processFlowService.downloadFile(json, `${flowName.replace(/\s+/g, '_')}.json`, 'application/json');
        setShowExportMenu(false);
    };

    const handleExportCSV = () => {
        const data = buildExportData();
        const csv = processFlowService.exportAsCSV(data);
        processFlowService.downloadFile(csv, `${flowName.replace(/\s+/g, '_')}.csv`, 'text/csv');
        setShowExportMenu(false);
    };

    const handleExportPNG = async () => {
        setShowExportMenu(false);
        try {
            const flowElement = document.querySelector('.react-flow__viewport') as HTMLElement;
            if (!flowElement) { alert('Could not find diagram element'); return; }
            const nodesBounds = getRectOfNodes(nodes);
            const imageWidth = nodesBounds.width + 100;
            const imageHeight = nodesBounds.height + 100;
            const transform = getTransformForBounds(nodesBounds, imageWidth, imageHeight, 0.5, 2);
            const { default: html2canvas } = await import('html2canvas');
            const wrapper = document.createElement('div');
            const bgColor = isDark ? '#0f172a' : '#ffffff';
            wrapper.style.cssText = `position:absolute;left:-10000px;top:0;width:${imageWidth}px;height:${imageHeight}px;background:${bgColor};overflow:hidden;`;
            const clone = flowElement.cloneNode(true) as HTMLElement;
            clone.style.transform = `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`;
            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);
            // The @types/html2canvas definition uses 'background' (lib differs from TS types)
            const canvas = await html2canvas(wrapper, { background: bgColor, width: imageWidth, height: imageHeight });
            document.body.removeChild(wrapper);
            const link = document.createElement('a');
            link.download = `${flowName.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (e: unknown) {
            alert(`PNG export failed: ${(e as Error).message}. Try installing html2canvas.`);
        }
    };

    const handleExportSVG = () => {
        setShowExportMenu(false);
        const nodesBounds = getRectOfNodes(nodes);
        const padding = 50;
        const width = nodesBounds.width + padding * 2;
        const height = nodesBounds.height + padding * 2;
        let svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${nodesBounds.x - padding} ${nodesBounds.y - padding} ${width} ${height}"><style>.node-rect{fill:#fff;stroke:#64748b;stroke-width:2}.node-text{font-family:Inter,sans-serif;font-size:12px;fill:#1e293b}.node-label{font-weight:bold}.edge-path{fill:none;stroke:#94a3b8;stroke-width:2}</style><rect x="${nodesBounds.x - padding}" y="${nodesBounds.y - padding}" width="${width}" height="${height}" fill="${isDark ? '#0f172a' : '#f8fafc'}"/>`;
        nodes.forEach(node => {
            const { x, y } = node.position;
            svg += `<g transform="translate(${x},${y})"><rect class="node-rect" x="0" y="0" width="200" height="80" rx="8"/><text class="node-text node-label" x="100" y="25" text-anchor="middle">${node.data?.stepNumber || ''}</text><text class="node-text" x="100" y="50" text-anchor="middle">${node.data?.label || ''}</text></g>`;
        });
        edges.forEach(edge => {
            const src = nodes.find(n => n.id === edge.source);
            const tgt = nodes.find(n => n.id === edge.target);
            if (src && tgt) svg += `<path class="edge-path" d="M${src.position.x + 100},${src.position.y + 80} L${tgt.position.x + 100},${tgt.position.y}" marker-end="url(#arrow)"/>`;
        });
        svg += `<defs><marker id="arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8"/></marker></defs></svg>`;
        processFlowService.downloadFile(svg, `${flowName.replace(/\s+/g, '_')}.svg`, 'image/svg+xml');
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) { alert('Please allow popups to print the diagram.'); return; }
        const nodesBounds = getRectOfNodes(nodes);
        const padding = 50;
        const width = nodesBounds.width + padding * 2;
        const height = nodesBounds.height + padding * 2;
        let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${nodesBounds.x - padding} ${nodesBounds.y - padding} ${width} ${height}"><style>.node-rect{fill:#fff;stroke:#64748b;stroke-width:2}.node-text{font-family:Inter,Arial,sans-serif;font-size:12px;fill:#1e293b}.node-label{font-weight:bold}.edge-path{fill:none;stroke:#94a3b8;stroke-width:2}</style><rect x="${nodesBounds.x - padding}" y="${nodesBounds.y - padding}" width="${width}" height="${height}" fill="#ffffff"/>`;
        nodes.forEach(node => {
            const { x, y } = node.position;
            svgContent += `<g transform="translate(${x},${y})"><rect class="node-rect" x="0" y="0" width="200" height="80" rx="8"/><text class="node-text node-label" x="100" y="25" text-anchor="middle">${node.data?.stepNumber || ''}</text><text class="node-text" x="100" y="50" text-anchor="middle">${node.data?.label || ''}</text></g>`;
        });
        edges.forEach(edge => {
            const src = nodes.find(n => n.id === edge.source);
            const tgt = nodes.find(n => n.id === edge.target);
            if (src && tgt) svgContent += `<path class="edge-path" d="M${src.position.x + 100},${src.position.y + 80} L${tgt.position.x + 100},${tgt.position.y}" marker-end="url(#arrow)"/>`;
        });
        svgContent += `<defs><marker id="arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8"/></marker></defs></svg>`;
        printWindow.document.write(`<!DOCTYPE html><html><head><title>${flowName} - Process Flow Diagram</title><style>body{margin:0;padding:20px;font-family:Inter,Arial,sans-serif}h1{font-size:18px;margin-bottom:10px}.meta{font-size:12px;color:#666;margin-bottom:20px}@media print{body{padding:0}}</style></head><body><h1>${flowName}</h1><div class="meta">Printed: ${new Date().toLocaleString()}</div>${svgContent}<script>window.onload=function(){window.print();window.close()}<\/script></body></html>`);
        printWindow.document.close();
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const imported = processFlowService.importFromJSON(text);
            const { x = 0, y = 0, zoom = 1 } = imported.flow.viewport || {};
            setNodes(imported.flow.nodes || []);
            setEdges(imported.flow.edges || []);
            setFlowName(imported.name + ' (Imported)');
            setFlowId(null);
            reactFlowInstance.setViewport({ x, y, zoom });
            alert('Flow imported successfully! Remember to Save.');
        } catch (e: unknown) {
            alert(`Failed to import: ${(e as Error).message}`);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── node/edge handlers ────────────────────────────────────────────────────
    const onConnect = useCallback((params: Connection) => {
        setEdges(eds => addEdge({ ...params, type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } }, eds));
    }, [setEdges]);

    const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
        setSelectedNodeId(node.id);
        setSelectedEdgeId(null);
    }, []);

    const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
        setSelectedEdgeId(edge.id);
        setSelectedNodeId(null);
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
    }, []);

    const handleAddNode = (type: ProcessStepType) => {
        const newCount = nodeCount + 1;
        setNodeCount(newCount);
        const id = `node-${Date.now()}`;
        // screenToFlowPosition is the correct API in ReactFlow v11+ (project() is deprecated)
        const position = reactFlowInstance.screenToFlowPosition({ x: window.innerWidth / 2 - 100, y: window.innerHeight / 2 - 50 });
        const newNode: ProcessFlowNodeType = {
            id, type: 'processFlowNode', position,
            data: { label: type === ProcessStepType.START_END ? 'Process End' : 'New Step', stepNumber: generateStepNumber(type, newCount), type, description: '' },
        };
        setNodes(nds => [...nds, newNode]);
    };

    const handleUpdateNode = (id: string, data: ProcessStepData) =>
        setNodes(nds => nds.map(node => node.id === id ? { ...node, data } : node));

    const handleUpdateEdge = (id: string, data: unknown) =>
        setEdges(eds => eds.map(edge => edge.id === id ? { ...edge, data } : edge));

    const handleDeleteNode = (id: string) => {
        setNodes(nds => nds.filter(n => n.id !== id));
        setEdges(eds => eds.filter(e => e.source !== id && e.target !== id));
        setSelectedNodeId(null);
    };

    const handleDeleteEdge = (id: string) => {
        setEdges(eds => eds.filter(e => e.id !== id));
        setSelectedEdgeId(null);
    };

    const handleLoadTemplate = (templateKey: string) => {
        const template = PROCESS_FLOW_TEMPLATES[templateKey];
        if (template) {
            setNodes(template.nodes as unknown as Node[]);
            setEdges(template.edges as unknown as Edge[]);
            setShowTemplateMenu(false);
            setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 100);
        }
    };

    const handleAutoLayout = () => {
        const sorted = [...nodes].sort((a, b) => a.position.y - b.position.y);
        setNodes(sorted.map((node, i) => ({ ...node, position: { x: 250, y: i * 150 } })));
        setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 100);
    };

    // ── work element handlers ─────────────────────────────────────────────────
    const handleAddWorkElement = (stepId: string) => {
        const newElement: WorkElement = {
            id: `we-${Date.now()}`,
            name: `Work Element ${workElements.filter(we => we.processStepId === stepId).length + 1}`,
            description: '',
            processStepId: stepId,
            duration: 0,
        };
        setWorkElements(prev => [...prev, newElement]);
    };

    const handleUpdateWorkElement = (elementId: string, updates: Partial<WorkElement>) =>
        setWorkElements(prev => prev.map(we => we.id === elementId ? { ...we, ...updates } : we));

    const handleDeleteWorkElement = (elementId: string) => {
        if (window.confirm('Delete this work element?'))
            setWorkElements(prev => prev.filter(we => we.id !== elementId));
    };

    // ── process item handlers ─────────────────────────────────────────────────
    const handleAddProcessItem = () => {
        const newItem: ProcessItem = {
            id: `pi-${Date.now()}`,
            name: `Process Item ${processItems.length + 1}`,
            description: 'New process item',
            processStepIds: [],
            projectName: activeProjectName || 'Unnamed Project',
            order: processItems.length + 1,
        };
        setProcessItems(prev => [...prev, newItem]);
    };

    const handleEditItem = (type: string, id: string) => {
        if (type === 'item') {
            const item = processItems.find(pi => pi.id === id);
            if (item) {
                const newName = prompt('Enter new name:', item.name);
                if (newName) setProcessItems(prev => prev.map(pi => pi.id === id ? { ...pi, name: newName } : pi));
            }
        } else if (type === 'step') {
            setSelectedNodeId(id);
            setSelectedEdgeId(null);
        } else if (type === 'element') {
            const element = workElements.find(we => we.id === id);
            if (element) {
                const newName = prompt('Enter new name:', element.name);
                if (newName) handleUpdateWorkElement(id, { name: newName });
            }
        }
    };

    const handleDeleteItem = (type: string, id: string) => {
        if (type === 'item' && confirm('Delete this Process Item?'))
            setProcessItems(prev => prev.filter(pi => pi.id !== id));
        else if (type === 'step' && confirm('Delete this Process Step?'))
            handleDeleteNode(id);
        else if (type === 'element')
            handleDeleteWorkElement(id);
    };

    const handleExtract = () => {
        if (nodes.length > 1) {
            extractFromFlow(nodes);
            alert(`Extracted ${nodes.length - 1} process steps to Analysis.`);
        } else {
            alert('Add process steps to the diagram first.');
        }
    };

    return {
        // theme
        isDark,
        // canvas
        nodes, edges, onNodesChange, onEdgesChange,
        selectedNode, selectedEdge,
        onConnect, onNodeClick, onEdgeClick, onPaneClick,
        // UI state
        flowName, setFlowName,
        showTemplateMenu, setShowTemplateMenu,
        showLoadMenu, setShowLoadMenu,
        showExportMenu, setShowExportMenu,
        showStructureTree, setShowStructureTree,
        savedFlows,
        isSaving,
        // RBAC
        canEdit, canCreate,
        // refs
        templateMenuRef, loadMenuRef, exportMenuRef, fileInputRef,
        // history
        history, historyIndex, clipboard,
        // handlers - save/load
        handleSave, handleLoadFlow, handleDeleteFlow,
        // handlers - export
        handleExportJSON, handleExportCSV, handleExportPNG, handleExportSVG, handlePrint,
        // handlers - import
        handleImportClick, handleFileImport,
        // handlers - edit
        handleUndo, handleRedo, handleCopy, handlePaste,
        handleAddNode, handleUpdateNode, handleUpdateEdge, handleDeleteNode, handleDeleteEdge,
        handleLoadTemplate, handleAutoLayout,
        // handlers - work elements / items
        workElements, processItems,
        handleAddWorkElement, handleUpdateWorkElement, handleDeleteWorkElement,
        handleAddProcessItem, handleEditItem, handleDeleteItem,
        handleExtract,
    };
}
